
import React from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  category: string;
  image: string;
  description: string;
  rating: number;
  reviews: number;
  pricing: string;
}

interface RelatedProductsProps {
  products: Product[];
}

/**
 * Renders a grid of related products.
 * Includes basic defensive checks and error handling to avoid runtime crashes
 * when the `products` prop is missing or malformed.
 */
export default function RelatedProducts({ products }: RelatedProductsProps) {
  // Guard against undefined or non‑array `products`
  if (!Array.isArray(products) || products.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Related Products</h2>
          <p className="text-gray-500 text-sm">No related products available.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Related Products</h2>
            <p className="text-gray-500 text-sm">More tools in the same category</p>
          </div>
          <Link
            to="/products"
            className="text-teal-600 hover:text-teal-700 text-sm font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            View All
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1 cursor-pointer border border-gray-100 block"
            >
              <div className="relative h-48 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  title={`${product.name} - ${product.category}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="px-2.5 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full uppercase">
                  {product.category}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mt-3 mb-1">{product.name}</h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{product.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400 text-sm" aria-hidden="true"></i>
                    <span className="text-sm font-semibold text-gray-800">{product.rating}</span>
                    <span className="text-xs text-gray-400">
                      ({product.reviews?.toLocaleString?.() ?? product.reviews})
                    </span>
                  </div>
                  <span className="text-sm font-bold text-teal-600">{product.pricing}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
